/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import throttle from 'lodash/throttle';

/**
 * Like `useDebounce`, but also returns `flush`, which sets the debounced value
 * right away instead of waiting out the delay. Useful when the value jumps to a
 * new starting point (a form reset, a dialog reopening) that shouldn't wait
 * behind the old value.
 */
export function useFlushableDebounce<T>(value: T, delay: number) {
    const [debouncedValue, setDebouncedValue] = React.useState(value);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return [debouncedValue, setDebouncedValue] as const;
}

export function useDebounce<T>(value: T, delay: number) {
    return useFlushableDebounce(value, delay)[0];
}

export function useThrottle<T>(value: T, delay: number = 750) {
    const [throttledValue, setThrottledValue] = React.useState(value);

    React.useEffect(() => {
        const throttled = throttle((v) => {
            setThrottledValue(v);
        }, delay);

        throttled(value);

        return () => {
            throttled.cancel();
        };
    }, [value, delay]);

    return throttledValue;
}
